import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type JSX } from "react";

import { VERTICALS, labelForVerticalSlug, type SalesLeadFocus, type SalesLeadFocusInput } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";

/** Countries offered in the picker — the Middle East first (the current hunt),
 * then the rest of the markets the agent works. Any ISO-2 code can still be
 * typed; the agent validates it. */
export const FOCUS_COUNTRIES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "SA", name: "Saudi Arabia" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "KW", name: "Kuwait" },
  { code: "QA", name: "Qatar" },
  { code: "BH", name: "Bahrain" },
  { code: "OM", name: "Oman" },
  { code: "JO", name: "Jordan" },
  { code: "IQ", name: "Iraq" },
  { code: "EG", name: "Egypt" },
  { code: "TR", name: "Turkey" },
  { code: "IL", name: "Israel" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IN", name: "India" },
  { code: "MX", name: "Mexico" },
  { code: "AU", name: "Australia" },
];

export function countryLabel(code: string): string {
  return FOCUS_COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

/**
 * Sales ▸ Status — WHERE the Lead Generator hunts (2026-09-23).
 *
 * While a focus is on, the agent searches only these countries × industries,
 * never turns the harvester pool into leads, and CHECKS every candidate before
 * saving it: only a verified, high-scoring one becomes a lead. The card shows
 * how many candidates that check has refused — the pile that used to be
 * created and then dismissed.
 */
export function LeadFocusPanel(): JSX.Element | null {
  const canEdit = useCan("sales:edit");
  const [open, setOpen] = useState(false);
  const focus = useQuery({
    queryKey: ["sales", "lead-focus"],
    queryFn: salesApi.getLeadFocus,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });

  if (!focus.data) return null;
  const f = focus.data;

  return (
    <Card data-testid="lead-focus-panel">
      <CardContent sx={{ display: "grid", gap: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600 }}>
            Lead focus
          </Typography>
          <Chip
            size="small"
            label={f.active ? "On" : "Off — searching everywhere"}
            data-testid="lead-focus-state"
            sx={f.active
              ? { bgcolor: (t) => alpha(t.palette.success.main, 0.15), color: "success.main", fontWeight: 600 }
              : { bgcolor: "surfaceSoft", color: "text.secondary" }}
          />
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }} data-testid="lead-focus-refused">
              {f.refused_candidates} candidate{f.refused_candidates === 1 ? "" : "s"} refused before saving
            </Typography>
            {canEdit && (
              <Button size="small" variant="outlined" onClick={() => setOpen(true)} data-testid="lead-focus-edit">
                Edit
              </Button>
            )}
          </Box>
        </Box>
        {f.active ? (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {f.countries.map((c) => <Chip key={c} size="small" variant="outlined" label={countryLabel(c)} />)}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {f.verticals.map((v) => <Chip key={v} size="small" label={labelForVerticalSlug(v)} />)}
            </Box>
            {f.brief && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{f.brief}</Typography>}
          </>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            No focus set — the Lead Generator scans every market it knows. Every candidate is still checked before it is saved.
          </Typography>
        )}
      </CardContent>
      {canEdit && <LeadFocusDialog open={open} focus={f} onClose={() => setOpen(false)} />}
    </Card>
  );
}

function LeadFocusDialog({ open, focus, onClose }: { open: boolean; focus: SalesLeadFocus; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<SalesLeadFocusInput>(focus);
  useEffect(() => {
    if (open) setDraft({ active: focus.active, countries: focus.countries, verticals: focus.verticals, brief: focus.brief });
  }, [open, focus]);

  const save = useMutation({
    mutationFn: () => salesApi.setLeadFocus(draft),
    onSuccess: (next) => {
      qc.setQueryData(["sales", "lead-focus"], next);
      qc.invalidateQueries({ queryKey: ["sales", "today"] });
      onClose();
    },
  });

  const invalid = draft.active && (draft.countries.length === 0 || draft.verticals.length === 0);

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Lead focus</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
        <FormControlLabel
          control={
            <Switch
              checked={draft.active}
              onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))}
              inputProps={{ "aria-label": "Focus on" }}
            />
          }
          label="Search only this area"
        />
        <Autocomplete
          multiple
          freeSolo
          options={FOCUS_COUNTRIES.map((c) => c.code)}
          getOptionLabel={(code) => `${countryLabel(code)} (${code})`}
          value={draft.countries}
          onChange={(_, v) => setDraft((d) => ({ ...d, countries: v.map((x) => x.trim().toUpperCase()).filter(Boolean) }))}
          renderInput={(params) => <TextField {...(params as TextFieldProps)} label="Countries" placeholder="Add a country" />}
          data-testid="lead-focus-countries"
        />
        <Autocomplete
          multiple
          options={VERTICALS.map((v) => v.slug)}
          getOptionLabel={(slug) => labelForVerticalSlug(slug)}
          value={draft.verticals}
          onChange={(_, v) => setDraft((d) => ({ ...d, verticals: v }))}
          renderInput={(params) => <TextField {...(params as TextFieldProps)} label="Industries" placeholder="Add an industry" />}
          data-testid="lead-focus-verticals"
        />
        <TextField
          label="Brief for the agent"
          multiline
          minRows={3}
          value={draft.brief}
          onChange={(e) => setDraft((d) => ({ ...d, brief: e.target.value }))}
          inputProps={{ maxLength: 1500 }}
          helperText="What makes a good lead here — the agent reads this on every search."
        />
        {save.isError && (
          <Box role="alert" sx={{ borderRadius: 1, bgcolor: (t) => alpha(t.palette.error.main, 0.1), px: 1.5, py: 1, fontSize: 12, color: "error.main" }}>
            {save.error instanceof Error ? save.error.message : "Could not save the focus"}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => save.mutate()}
          disabled={save.isPending || invalid}
          data-testid="lead-focus-save"
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
